import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faFolder } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Box from "@mui/material/Box";
import { isBlurhashValid } from "blurhash";
import { useState } from "react";
import { BlurHashCanvas } from "../blurHashCanvas";

/**
 * Circular space avatar: the image crossfades in over the blurhash when one is
 * known, over the tinted disc otherwise, and a missing avatar or a load
 * failure falls back to the blurhash or the themed folder glyph, never to an
 * error state. The optional intro props let the invitation page hold the
 * reveal and play its highlight sweep; `withRing` mirrors how drive-client
 * marks the active space.
 */
export function SpaceAvatar({
  url,
  size,
  iconFontSize,
  withRing = false,
  blurhash,
  holdReveal = false,
  shimmer = false,
  onImageLoaded,
}: {
  /** Avatar image URL; the blurhash or folder glyph shows without one. */
  url?: string | null;
  /** Disc diameter, plain or responsive. */
  size: number | Partial<Record<"xs" | "sm" | "md", number>>;
  /** Size of the fallback glyph. */
  iconFontSize: number;
  /** White ring and soft shadow around the disc. */
  withRing?: boolean;
  /** Placeholder while the image loads, richer fallback after. */
  blurhash?: string | null;
  /** Keeps the blurhash on top even after the image loaded (intro). */
  holdReveal?: boolean;
  /** Plays the one-time highlight sweep. */
  shimmer?: boolean;
  onImageLoaded?: () => void;
}) {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const showImage = !!url && !failed;
  const revealed = loaded && !holdReveal;
  // A malformed hash from the server counts as no hash: the glyph fallback
  // takes over instead of decode() throwing during render.
  const safeHash =
    blurhash && isBlurhashValid(blurhash).result ? blurhash : undefined;

  return (
    <Box
      sx={{
        position: "relative",
        flex: "none",
        width: size,
        height: size,
        borderRadius: "50%",
        overflow: "hidden",
        userSelect: "none",
        bgcolor: "color-mix(in srgb, var(--primary-color) 12%, transparent)",
        ...(withRing
          ? {
              border: "4px solid #ffffff",
              boxShadow: "0px 4px 16px #00000014, 0px 0px 2px #00000029",
            }
          : { boxShadow: "0px 0px 2px #00000029" }),
      }}
    >
      {safeHash && (
        <div
          className={`avatar-blurhash ${
            revealed ? "avatar-blurhash-hidden" : ""
          }`}
        >
          <BlurHashCanvas
            hash={safeHash}
            className="absolute inset-0 h-full w-full"
          />
        </div>
      )}
      {showImage && (
        <img
          src={url}
          alt=""
          draggable={false}
          loading="lazy"
          onDragStart={(event) => event.preventDefault()}
          onLoad={() => {
            setLoaded(true);
            onImageLoaded?.();
          }}
          onError={() => setFailed(true)}
          className={`avatar-reveal absolute inset-0 h-full w-full object-cover ${
            revealed ? "avatar-reveal-loaded" : ""
          }`}
        />
      )}
      {shimmer && <div className="avatar-shimmer-once" />}
      {!showImage && !safeHash && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--primary-color)",
          }}
        >
          <FontAwesomeIcon
            icon={faFolder as IconProp}
            style={{ fontSize: iconFontSize }}
          />
        </Box>
      )}
    </Box>
  );
}
